/**
 * DataKeyStep Component
 * Step 2B: Ask for the data key to use in the macro
 */

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Key, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react'
import { Platform } from '../../../types/macros'
import { validateMacroKey } from '../../../utils/macroHelpers'

interface DataKeyStepProps {
  platform: Platform | null
  dataKey: string
  onDataKeyChange: (key: string) => void
  onBack: () => void
}

const DataKeyStep: React.FC<DataKeyStepProps> = ({
  platform,
  dataKey,
  onDataKeyChange,
  onBack
}) => {
  const [validation, setValidation] = useState<{ isValid: boolean; errors: string[] }>({
    isValid: false,
    errors: []
  })
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (dataKey) {
      setValidation(validateMacroKey(dataKey))
      setTouched(true)
    } else {
      setValidation({ isValid: false, errors: [] })
    }
  }, [dataKey])

  // All available keys
  const allKeys = [
    { key: 'firstname', label: 'First Name', example: 'John', shopifySupported: true },
    { key: 'lastname', label: 'Last Name', example: 'Smith', shopifySupported: true },
    { key: 'email', label: 'Email', example: 'john@example.com', shopifySupported: false },
    { key: 'confirmationref', label: 'Order ID', example: 'ORD-12345', shopifySupported: false },
    { key: 'amount', label: 'Amount', example: '$25.00', shopifySupported: false },
    { key: 'orderid', label: 'Order Number', example: '98765', shopifySupported: false }
  ]

  // Filter keys based on platform
  const commonKeys = platform === Platform.SHOPIFY
    ? allKeys.filter(k => k.shopifySupported)
    : allKeys

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-[#6E6D7A] hover:text-[#1A2B49] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to context selection
        </button>
        
        <h2 className="text-2xl font-bold text-[#1A2B49] mb-2">
          What information do you want to show?
        </h2>
        <p className="text-[#6E6D7A]">
          {platform === Platform.SHOPIFY
            ? 'For Shopify integration, only first_name and last_name are supported.'
            : 'Enter the data key that contains the information you want to display in your message.'
          }
        </p>
      </div>

      {/* Shopify Warning */}
      {platform === Platform.SHOPIFY && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-amber-900 font-medium mb-1">Shopify Limitation</p>
              <p className="text-amber-700">
                Only <code className="px-1.5 py-0.5 bg-amber-100 rounded text-xs">{`{{first_name}}`}</code> and{' '}
                <code className="px-1.5 py-0.5 bg-amber-100 rounded text-xs">{`{{last_name}}`}</code> are available.
                These render only when customers are logged in.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Input */}
      <div>
        <label className="block text-sm font-medium text-[#1A2B49] mb-3">
          Data Key
        </label>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Key className="h-5 w-5 text-[#6E6D7A]" />
          </div>
          
          <input
            type="text"
            value={dataKey}
            onChange={(e) => onDataKeyChange(e.target.value.toLowerCase().replace(/\s/g, '_'))}
            placeholder="e.g., firstname, orderid, amount"
            className={`w-full pl-10 pr-12 py-4 text-lg border-2 rounded-lg focus:outline-none transition-colors ${
              touched && dataKey
                ? validation.isValid
                  ? 'border-green-500 focus:border-green-600 bg-green-50'
                  : 'border-red-500 focus:border-red-600 bg-red-50'
                : 'border-gray-300 focus:border-[#FE4215]'
            }`}
          />
          
          {touched && dataKey && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {validation.isValid ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-500" />
              )}
            </div>
          )}
        </div>

        {/* Validation Messages */}
        {touched && validation.errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 space-y-1"
          >
            {validation.errors.map((error, index) => (
              <div key={index} className="flex items-start space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Success Message */}
        {touched && validation.isValid && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 flex items-center space-x-2 text-green-600 text-sm"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Valid key format! Your macro will be: <code className="bg-green-100 px-2 py-0.5 rounded font-mono">{`{{${dataKey}}}`}</code></span>
          </motion.div>
        )}

        {/* Rules */}
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs font-medium text-blue-900 mb-2">Key Format Rules:</p>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Use lowercase letters, numbers, and underscores only</li>
            <li>• Must start with a letter or underscore</li>
            <li>• No spaces allowed (use underscores instead)</li>
            <li>• Maximum 50 characters</li>
          </ul>
        </div>
      </div>

      {/* Common Keys */}
      <div>
        <h3 className="text-sm font-medium text-[#1A2B49] mb-3">
          Or choose a common key:
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {commonKeys.map((item) => (
            <motion.button
              key={item.key}
              onClick={() => onDataKeyChange(item.key)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                dataKey === item.key
                  ? 'border-[#FE4215] bg-[#FE4215]/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-[#1A2B49] mb-1 text-sm">{item.label}</div>
              <code className="text-xs text-[#6E6D7A] font-mono">{item.key}</code>
              <div className="mt-2 text-xs text-[#6E6D7A]">
                Example: {item.example}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Example Usage */}
      {validation.isValid && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200"
        >
          <h3 className="text-sm font-semibold text-[#1A2B49] mb-2">
            How this will work:
          </h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-[#6E6D7A]">If data exists:</span>
              <code className="block mt-1 p-2 bg-white rounded text-green-700 font-mono text-xs">
                "Your order #12345" → {`{{${dataKey}}}`} → "Your order #12345"
              </code>
            </div>
            <div>
              <span className="text-[#6E6D7A]">If data doesn't exist:</span>
              <code className="block mt-1 p-2 bg-white rounded text-gray-500 font-mono text-xs">
                "Your order " → {`{{${dataKey}}}`} → "Your order "
              </code>
            </div>
          </div>
          <p className="text-xs text-[#6E6D7A] mt-3">
            💡 In the next step, you can choose what happens when data is missing
          </p>
        </motion.div>
      )}
    </div>
  )
}

export default DataKeyStep
